/**
 * BookingFormWrapper Component
 *
 * Container component that manages which booking form to display (add/edit/delete)
 * based on the current mode. Handles loading states for async booking fetches.
 *
 * Ported from Vue BookingFormWrapper.vue
 *
 * @example
 * ```tsx
 * <BookingFormWrapper
 *   mode="add"
 *   targetEntity={selectedEmployee}
 *   selectedCards={selectedCards}
 *   bookingType="hard"
 *   onCancel={() => setFormOpen(false)}
 *   onDone={(msg) => showSuccess(msg)}
 *   onError={(msg) => showError(msg)}
 * />
 * ```
 */

import React, { useCallback, useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { Spinner } from "@/components/ui/feedback/Spinner";
import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { Icon } from "@/components/ui/display/Icon";
import { AddForm } from "@/components/booking/forms/AddForm";
import { DeleteForm } from "@/components/booking/forms/DeleteForm";
import { EditForm } from "@/components/booking/forms/EditForm";
import { hoursInDayAtom } from "@/store/userStore";
import { cx } from "@/utils/cx";
import type { BookingFormWrapperProps, EditMode, LoadingState } from "./types";
import styles from "./BookingFormWrapper.module.css";
import sharedStyles from "../shared/formUtilities.module.css";

export const BookingFormWrapper: React.FC<BookingFormWrapperProps> = ({
  mode,
  targetEntity,
  selectedCards = [],
  bookingType = "hard",
  onCancel,
  onDone,
  onError,
  onClose,
  absenceDisabled = false,
  className = "",
  "data-testid": testId,
}) => {
  // Get hours per day from organization settings
  const hoursInDay = useAtomValue(hoursInDayAtom);

  // Loading state for async booking fetches
  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: false,
    loadedCount: 0,
    totalCount: 0,
    failedToLoad: false,
  });

  // Handle done - calls onDone and onClose
  const handleDone = useCallback(
    (message: string) => {
      onDone?.(message);
      onClose?.();
    },
    [onDone, onClose],
  );

  // Handle error - calls onError (but doesn't close - let user retry)
  const handleError = useCallback(
    (message: string) => {
      onError?.(message);
    },
    [onError],
  );

  // Retry loading async bookings
  const handleRetry = useCallback(() => {
    setLoadingState((prev) => ({
      ...prev,
      failedToLoad: false,
      isLoading: true,
    }));
    // In the real implementation, this would trigger a re-fetch
    // For now, simulate success after a short delay
    setTimeout(() => {
      setLoadingState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }, 500);
  }, []);

  // Reset loading state when mode changes
  useEffect(() => {
    setLoadingState({
      isLoading: false,
      loadedCount: 0,
      totalCount: 0,
      failedToLoad: false,
    });
  }, [mode]);

  // Generate loading text
  const loadingText =
    loadingState.totalCount > 0
      ? `Finner bookinger... (${loadingState.loadedCount}/${loadingState.totalCount})`
      : "Laster...";

  // Render form based on mode
  const renderForm = (currentMode: EditMode) => {
    switch (currentMode) {
      case "add":
        return (
          <AddForm
            targetEntity={targetEntity}
            selectedCards={selectedCards}
            bookingType={bookingType}
            defaultHours={hoursInDay}
            absenceDisabled={absenceDisabled}
            onCancel={onCancel}
            onDone={handleDone}
            onError={handleError}
            data-testid="add-form"
          />
        );
      case "edit":
        return (
          <EditForm
            targetEntity={targetEntity}
            selectedCards={selectedCards}
            bookingType={bookingType}
            defaultHours={hoursInDay}
            absenceDisabled={absenceDisabled}
            onCancel={onCancel}
            onDone={handleDone}
            onError={handleError}
            data-testid="edit-form"
          />
        );
      case "delete":
        return (
          <DeleteForm
            selectedCards={selectedCards}
            bookingType={bookingType}
            onCancel={onCancel}
            onDone={handleDone}
            onError={handleError}
            data-testid="delete-form"
          />
        );
      default:
        return null;
    }
  };

  // Build class names
  const containerClasses = cx(styles.formContent, className);

  return (
    <div className={containerClasses} data-testid={testId}>
      {/* Error state - failed to load async bookings */}
      {loadingState.failedToLoad && (
        <div className={styles.errorContainer} data-testid="form-error-state">
          <p className={styles.errorText}>Kunne ikke hente nodvendig data.</p>
          <ActionButton
            variant="primary"
            label="Prov pa nytt"
            icon={<Icon name="refresh" size="sm" />}
            onClick={handleRetry}
            data-testid="form-retry-button"
          />
        </div>
      )}

      {/* Loading state - fetching async bookings */}
      {!loadingState.failedToLoad && loadingState.isLoading && (
        <div
          className={sharedStyles.loadingContainer}
          data-testid="form-loading-state"
        >
          <Spinner size="lg" variant="primary" centered />
          <span className={sharedStyles.loadingText}>{loadingText}</span>
        </div>
      )}

      {/* Form content - render appropriate form based on mode */}
      {!loadingState.failedToLoad &&
        !loadingState.isLoading &&
        renderForm(mode)}
    </div>
  );
};

BookingFormWrapper.displayName = "BookingFormWrapper";

export default BookingFormWrapper;
